// Patent claims exercised: P-001 Claims 1-7 (user health profile aggregation)
import { API_BASE } from "../config";
/**
 * ProfileTab.jsx
 * Profile management tab containing Body Characteristics and Sports/Hobbies
 * CSS EXTRACTED — all styles in healist-app-components.css (pt- prefix)
 * 
 * CORRECT FIELD NAMES matching database schema:
 * - height, height_unit (NOT height_inches)
 * - weight, weight_unit (NOT weight_lbs)
 * - waist_circumference, waist_unit
 * - estimated_bmi, recommended_bmi, actual_bmi
 *
 * REMAINING INLINE STYLES (4 — dynamic JS values):
 * 1. BMI estimated value color — bmiColors[displayCategory]
 * 2. BMI category badge backgroundColor — bmiColors + alpha
 * 3. Actual BMI value color — bmiColors[getBmiCategory()]
 * 4. Calculate button opacity — calculating state
 */

import React, { useState, useEffect } from 'react';
import SportsHobbiesManager from './SportsHobbiesManager';
import HealistScoreDisplay from './HealistScoreDisplay';
import { CitationsBlock } from './Citations';

const ProfileTab = ({ token, fetchWithAuth, sectionOnly }) => {
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  // State
  const [activeSection, setActiveSection] = useState(sectionOnly || 'body');
  const [bodyData, setBodyData] = useState({
    height: '',
    height_unit: 'inches',
    weight: '',
    weight_unit: 'lbs',
    waist_circumference: '',
    waist_unit: 'inches',
    age: '',
    date_of_birth: '',
    birth_gender: 'Unspecified',
    blood_type: 'Unknown',
    cardio_exercise_level: 0,
    weight_training_level: 0,
    actual_bmi: ''
  });
  const [bmiData, setBmiData] = useState({
    estimated_bmi: null,
    recommended_bmi: null,
    bmi_category: null,
    health_notes: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch body characteristics on mount
  useEffect(() => {
    fetchBodyCharacteristics();
  }, []);

  const fetchBodyCharacteristics = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/profile/body-characteristics`);
      
      if (!response.ok) throw new Error('Failed to fetch body characteristics');
      
      const data = await response.json();
      if (data.body_characteristics) {
        const bc = data.body_characteristics;
        setBodyData({
          height: bc.height || '',
          height_unit: bc.height_unit || 'inches',
          weight: bc.weight || '',
          weight_unit: bc.weight_unit || 'lbs',
          waist_circumference: bc.waist_circumference || '',
          waist_unit: bc.waist_unit || 'inches',
          age: bc.age || '',
          date_of_birth: bc.date_of_birth ? bc.date_of_birth.substring(0, 10) : '',
          birth_gender: bc.birth_gender || 'Unspecified',
          blood_type: bc.blood_type || 'Unknown',
          cardio_exercise_level: bc.cardio_exercise_level || 0,
          weight_training_level: bc.weight_training_level || 0,
          actual_bmi: bc.actual_bmi || ''
        });
        if (bc.estimated_bmi || bc.recommended_bmi) {
          setBmiData({
            estimated_bmi: bc.estimated_bmi,
            recommended_bmi: bc.recommended_bmi,
            bmi_category: bc.bmi_category,
            health_notes: bc.health_notes
          });
        }
      }
    } catch (err) {
      console.error('Error fetching body characteristics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBodyChange = (field, value) => {
    setBodyData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveBodyCharacteristics = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`${API_BASE}/api/profile/body-characteristics`, {
        method: 'PUT',
        body: JSON.stringify(bodyData)
      });
      
      if (!response.ok) throw new Error('Failed to save body characteristics');
      
      const data = await response.json();
      setHasChanges(false);
      
      if (data.body_characteristics) {
        const bc = data.body_characteristics;
        // BMI is computed by the server from the saved height/weight — show its value.
        setBodyData(prev => ({ ...prev, actual_bmi: bc.actual_bmi || '' }));
        if (bc.estimated_bmi || bc.recommended_bmi) {
          setBmiData({
            estimated_bmi: bc.estimated_bmi,
            recommended_bmi: bc.recommended_bmi,
            bmi_category: bc.bmi_category,
            health_notes: bc.health_notes
          });
        }
      }
    } catch (err) {
      console.error('Error saving body characteristics:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const calculateBMI = async () => {
    setCalculating(true);
    setError(null);
    try {
      await saveBodyCharacteristics();
      
      const response = await authFetch(`${API_BASE}/api/profile/body-characteristics/calculate-bmi`, {
        method: 'POST',
        body: JSON.stringify(bodyData)
      });
      
      if (!response.ok) throw new Error('BMI calculation failed');
      
      const data = await response.json();
      setBmiData({
        estimated_bmi: data.estimated_bmi,
        recommended_bmi: data.recommended_bmi,
        bmi_category: data.bmi_category,
        health_notes: data.health_notes
      });
    } catch (err) {
      console.error('BMI calculation error:', err);
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  };

  // BMI color coding
  const bmiColors = {
    'Underweight': '#FF9800',
    'Normal': '#4CAF50',
    'Overweight': '#FF9800',
    'Obese': '#f44336',
    'Severely Obese': '#d32f2f'
  };

  // WHO adult categories — the SAME 4 bands as the server (backend/app/services/bmi.py
  // bmi_category). This used to add a 5th "Severely Obese" band at 35, so on page load (when the
  // GET carries no server category) the screen could disagree with the server, and the WHO
  // citation shown for the category would not describe the scale on screen (S4, 2026-10-17).
  const getBmiCategory = (bmi) => {
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  const displayCategory = bmiData.bmi_category || 
    (bmiData.estimated_bmi ? getBmiCategory(bmiData.estimated_bmi) : null);

  const exerciseLevels = [
    { value: 0, label: 'None' },
    { value: 1, label: 'Beginner' },
    { value: 2, label: 'Intermediate' },
    { value: 3, label: 'Advanced' },
    { value: 4, label: 'Elite' },
    { value: 5, label: 'Professional' }
  ];

  if (loading) {
    return <div className="pt-loading">Loading profile data...</div>;
  }

  return (
    <div className="pt-container">
      {/* Header + Tabs — hidden when embedded as single section */}
      {!sectionOnly && (
        <>
          <div className="pt-header">
            <div className="pt-title">My Profile</div>
            <div className="pt-subtitle">
              Manage your body characteristics and health profile for personalized recommendations
            </div>
          </div>

          <div className="pt-tabs">
            <button
              className={`pt-tab${activeSection === 'body' ? ' pt-tab--active' : ''}`}
              onClick={() => setActiveSection('body')}
            >
              Body Characteristics
            </button>
            <button
              className={`pt-tab${activeSection === 'sports' ? ' pt-tab--active' : ''}`}
              onClick={() => setActiveSection('sports')}
            >
              Sports & Hobbies
            </button>
            <button
              className={`pt-tab${activeSection === 'health' ? ' pt-tab--active' : ''}`}
              onClick={() => setActiveSection('health')}
            >
              Healist Score
            </button>
          </div>
        </>
      )}

      {/* Error */}
      {error && (
        <div className="pt-error">
          {error}
        </div>
      )}

      {/* Body Characteristics Section */}
      {activeSection === 'body' && (
        <div className="pt-section">
          <div className="pt-sectionTitle">Body Characteristics</div>

          <div className="pt-formGrid">
            {/* Height */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Height</label>
              <div className="pt-inputRow">
                <input
                  type="number"
                  className="pt-input"
                  min="36"
                  max="260"
                  value={bodyData.height}
                  onChange={(e) => handleBodyChange('height', e.target.value)}
                  placeholder={bodyData.height_unit === 'inches' ? 'e.g., 70' : 'e.g., 178'}
                />
                <div className="pt-unitToggle">
                  <button
                    className={`pt-unitButton pt-unitButton--left${bodyData.height_unit === 'inches' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('height_unit', 'inches')}
                  >
                    in
                  </button>
                  <button
                    className={`pt-unitButton pt-unitButton--right${bodyData.height_unit === 'cm' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('height_unit', 'cm')}
                  >
                    cm
                  </button>
                </div>
              </div>
            </div>

            {/* Weight */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Weight</label>
              <div className="pt-inputRow">
                <input
                  type="number"
                  className="pt-input"
                  min="20"
                  max="999"
                  value={bodyData.weight}
                  onChange={(e) => handleBodyChange('weight', e.target.value)}
                  placeholder={bodyData.weight_unit === 'lbs' ? 'e.g., 170' : 'e.g., 77'}
                />
                <div className="pt-unitToggle">
                  <button
                    className={`pt-unitButton pt-unitButton--left${bodyData.weight_unit === 'lbs' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('weight_unit', 'lbs')}
                  >
                    lbs
                  </button>
                  <button
                    className={`pt-unitButton pt-unitButton--right${bodyData.weight_unit === 'kg' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('weight_unit', 'kg')}
                  >
                    kg
                  </button>
                </div>
              </div>
            </div>

            {/* Waist */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Waist Circumference</label>
              <div className="pt-inputRow">
                <input
                  type="number"
                  className="pt-input"
                  min="20"
                  max="200"
                  value={bodyData.waist_circumference}
                  onChange={(e) => handleBodyChange('waist_circumference', e.target.value)}
                  placeholder={bodyData.waist_unit === 'inches' ? 'e.g., 34' : 'e.g., 86'}
                />
                <div className="pt-unitToggle">
                  <button
                    className={`pt-unitButton pt-unitButton--left${bodyData.waist_unit === 'inches' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('waist_unit', 'inches')}
                  >
                    in
                  </button>
                  <button
                    className={`pt-unitButton pt-unitButton--right${bodyData.waist_unit === 'cm' ? ' pt-unitButton--active' : ''}`}
                    onClick={() => handleBodyChange('waist_unit', 'cm')}
                  >
                    cm
                  </button>
                </div>
              </div>
            </div>

            {/* Date of Birth */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Date of Birth *</label>
              <input
                type="date"
                className="pt-input"
                value={bodyData.date_of_birth}
                onChange={(e) => {
                  const dob = e.target.value;
                  handleBodyChange('date_of_birth', dob);
                  if (dob) {
                    const ageDays = (new Date() - new Date(dob)) / (1000 * 60 * 60 * 24);
                    handleBodyChange('age', Math.floor(ageDays / 365.25));
                  }
                }}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Age (auto-calculated from DOB, or manual entry) */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Age</label>
              <input
                type="number"
                min="16"
                max="100"
                className="pt-input"
                value={bodyData.age}
                onChange={(e) => handleBodyChange('age', e.target.value)}
                placeholder="Auto-calculated from DOB"
                readOnly={!!bodyData.date_of_birth}
              />
            </div>

            {/* Birth Gender */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Birth Gender</label>
              <select
                className="pt-select"
                value={bodyData.birth_gender}
                onChange={(e) => handleBodyChange('birth_gender', e.target.value)}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Unspecified">Prefer not to say</option>
              </select>
              {bodyData.birth_gender === 'Unspecified' && (
                <p className="pt-fieldHint">Birth gender is required for accurate AI health recommendations.</p>
              )}
            </div>

            {/* Blood Type */}
            <div className="pt-fieldGroup">
              <label className="pt-label">Blood Type</label>
              <select
                className="pt-select"
                value={bodyData.blood_type}
                onChange={(e) => handleBodyChange('blood_type', e.target.value)}
              >
                <option value="Unknown">Unknown</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
              {bodyData.blood_type === 'Unknown' && (
                <p className="pt-fieldHint">Blood type helps personalize your health recommendations.</p>
              )}
            </div>

            {/* BMI — computed by the server from height + weight on save (services/bmi.py).
                Read-only: it used to be a free-entry "if known" box, and users onboarded by the
                interview never had one at all. */}
            <div className="pt-fieldGroup">
              <label className="pt-label">BMI</label>
              <input
                type="text"
                readOnly
                className="pt-input"
                value={bodyData.actual_bmi ? parseFloat(bodyData.actual_bmi).toFixed(1) : ''}
                placeholder="Calculated from height and weight when you save"
                aria-readonly="true"
              />
            </div>

            {/* Exercise Levels */}
            <div className="pt-formGrid">
              {/* Cardio Level */}
              <div className="pt-fieldGroup">
                <label className="pt-label">Cardio Exercise Level</label>
                <div className="pt-levelButtons">
                  {exerciseLevels.map(level => (
                    <button
                      key={`cardio-${level.value}`}
                      className={`pt-levelButton${bodyData.cardio_exercise_level === level.value ? ' pt-levelButton--active' : ''}`}
                      onClick={() => handleBodyChange('cardio_exercise_level', level.value)}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight Training Level */}
              <div className="pt-fieldGroup">
                <label className="pt-label">Weight Training Level</label>
                <div className="pt-levelButtons">
                  {exerciseLevels.map(level => (
                    <button
                      key={`weight-${level.value}`}
                      className={`pt-levelButton${bodyData.weight_training_level === level.value ? ' pt-levelButton--active' : ''}`}
                      onClick={() => handleBodyChange('weight_training_level', level.value)}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* BMI Section */}
          <div className="pt-bmiSection">
            <div className="pt-bmiHeader">
              <div className="pt-bmiTitle">BMI Analysis</div>
              <button
                className="pt-calculateButton"
                style={{ opacity: calculating ? 0.7 : 1 }}
                onClick={calculateBMI}
                disabled={calculating}
              >
                {calculating ? 'Calculating...' : 'Calculate BMI'}
              </button>
            </div>

            <div className="pt-bmiGrid">
              {/* Estimated BMI */}
              <div className="pt-bmiCard">
                <div className="pt-bmiValue" style={{ color: bmiColors[displayCategory] || '#fff' }}>
                  {bmiData.estimated_bmi ? bmiData.estimated_bmi.toFixed(1) : '--'}
                </div>
                <div className="pt-bmiLabel">Estimated BMI</div>
                {displayCategory && (
                  <div className="pt-bmiCategory" style={{ color: bmiColors[displayCategory], backgroundColor: `${bmiColors[displayCategory]}22` }}>
                    {displayCategory}
                  </div>
                )}
              </div>

              {/* "Recommended BMI" removed 2026-10-17 (D1): an uncited AI guess, no longer produced. */}

              {/* Actual BMI (if entered) */}
              {bodyData.actual_bmi && (
                <div className="pt-bmiCard">
                  <div
                    className="pt-bmiValue"
                    style={{ color: bmiColors[getBmiCategory(parseFloat(bodyData.actual_bmi))] || '#fff' }}
                  >
                    {parseFloat(bodyData.actual_bmi).toFixed(1)}
                  </div>
                  <div className="pt-bmiLabel">Actual BMI</div>
                </div>
              )}
            </div>

            {/* AI "Health Notes" removed 2026-10-17 (D1). The category above follows the WHO adult
                classification — Apple 1.4.1 (G15): its source, one tap away. */}
            {displayCategory && (
              <CitationsBlock featureName="BMI Classification" itemTitle={`BMI ${displayCategory}`}
                              testId="bmi-citations" />
            )}
          </div>

          {/* Save Bar */}
          <div className="pt-saveBar">
            <button
              className={`pt-saveButton${hasChanges ? ' pt-saveButton--active' : ''}`}
              onClick={saveBodyCharacteristics}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
            </button>
          </div>
        </div>
      )}

      {/* Sports & Hobbies Section */}
      {activeSection === 'sports' && (
        <SportsHobbiesManager token={token} />
      )}

      {/* Healist Score Section - Sprint 1.5 */}
      {activeSection === 'health' && (
        <div className="pt-healistScoreWrapper">
          <HealistScoreDisplay
            token={token}
            showRecommendations={true}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileTab;